/**
 * 剪贴板写入。
 * 浏览器要求在用户手势（click）内写剪贴板，因此同步方案 execCommand 优先，
 * 异步 API 作为兜底；任何异常都只返回 false，不打断后续的唤起/下载流程。
 */

/** 同步写入：隐藏 textarea + execCommand('copy')，必须在点击回调里同步调用 */
export function copyTextSync(text: string): boolean {
  if (!text || typeof document.execCommand !== 'function') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.setAttribute('aria-hidden', 'true');
  // 放在可视区外但保持可选中；font-size 16px 防止 iOS 聚焦时页面缩放
  Object.assign(textarea.style, {
    position: 'fixed',
    top: '0',
    left: '-9999px',
    opacity: '0',
    fontSize: '16px'
  });
  document.body.appendChild(textarea);

  const selection = document.getSelection();
  const originalRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  let ok = false;
  try {
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  } finally {
    document.body.removeChild(textarea);
    // 恢复用户原有选区
    if (originalRange && selection) {
      selection.removeAllRanges();
      selection.addRange(originalRange);
    }
  }
  return ok;
}

/** 异步写入：Clipboard API，需要安全上下文 */
export async function copyTextAsync(text: string): Promise<boolean> {
  if (!text || !navigator.clipboard?.writeText) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * 内容还没就绪时的写入：ClipboardItem 支持传入 Promise，
 * 在手势内同步发起 write，内容可以稍后 resolve（Safari 13.1+ / Chrome 新版本支持）
 */
export async function copyPendingText(textPromise: Promise<string>): Promise<boolean> {
  const ClipboardItemCtor = window.ClipboardItem;
  if (!ClipboardItemCtor || !navigator.clipboard?.write) return false;
  try {
    const blobPromise = textPromise.then((text) => {
      if (!text) throw new Error('empty clipboard token');
      return new Blob([text], { type: 'text/plain' });
    });
    await navigator.clipboard.write([new ClipboardItemCtor({ 'text/plain': blobPromise })]);
    return true;
  } catch {
    return false;
  }
}
